import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Search, CheckCircle2, XCircle, Clock, Shield,
  FileText, Calculator, ShieldCheck, BarChart3, Eye, Send,
  Loader2, Plus, ChevronRight, Users, AlertCircle, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface CompanyAccess {
  id: string;
  ca_user_id: string;
  company_name: string;
  company_gstin: string | null;
  company_admin_user_id: string | null;
  access_level: string;
  permissions: string[];
  status: string;
  requested_at: string;
  approved_at: string | null;
  notes: string | null;
}

interface Activity {
  id: string;
  activity_type: string;
  description: string;
  created_at: string;
  access_id: string;
}

const ACCESS_LEVELS = [
  {
    key: "view_only",
    label: "View Only",
    icon: Eye,
    desc: "Read-only access to ledgers, journals, and entries",
    permissions: ["view_accounting"],
  },
  {
    key: "accounting",
    label: "Accounting Access",
    icon: Calculator,
    desc: "View + approve entries and manage accounting data",
    permissions: ["view_accounting", "approve_entries"],
  },
  {
    key: "gst_filing",
    label: "GST Filing Access",
    icon: ShieldCheck,
    desc: "Accounting access + file GST returns and manage compliance",
    permissions: ["view_accounting", "approve_entries", "manage_gst"],
  },
  {
    key: "full_financial",
    label: "Full Financial Access",
    icon: BarChart3,
    desc: "Complete access including reports, GST, entries, and all financial data",
    permissions: ["view_accounting", "approve_entries", "manage_gst", "generate_reports"],
  },
];

const PERMISSION_OPTIONS = [
  { key: "view_accounting", label: "View Accounting Data", icon: Eye, desc: "View ledgers, journals, and entries" },
  { key: "approve_entries", label: "Approve Entries", icon: CheckCircle2, desc: "Review and approve accounting entries" },
  { key: "manage_gst", label: "Manage GST Filings", icon: ShieldCheck, desc: "File GST returns and manage compliance" },
  { key: "generate_reports", label: "Generate Reports", icon: BarChart3, desc: "Create financial and compliance reports" },
];

const statusConfig: Record<string, { label: string; cls: string; icon: typeof Clock }> = {
  pending: { label: "Pending", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20", icon: Clock },
  approved: { label: "Approved", cls: "bg-green-500/10 text-green-600 border-green-500/20", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle },
  revoked: { label: "Revoked", cls: "bg-muted text-muted-foreground border-border", icon: Lock },
};

function CompanyCard({ access, onView }: { access: CompanyAccess; onView: () => void }) {
  const status = statusConfig[access.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Card
        className={`bg-card border-border hover:border-accent/30 transition-all cursor-pointer group ${access.status !== "approved" ? "opacity-70" : ""}`}
        onClick={onView}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">{access.company_name}</h3>
                {access.company_gstin && (
                  <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{access.company_gstin}</code>
                )}
              </div>
            </div>
            <Badge variant="outline" className={`text-[10px] gap-1 ${status.cls}`}>
              <StatusIcon className="h-3 w-3" /> {status.label}
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {access.permissions.slice(0, 3).map((p) => {
                const opt = PERMISSION_OPTIONS.find((o) => o.key === p);
                return (
                  <Badge key={p} variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                    {opt?.label || p}
                  </Badge>
                );
              })}
              {access.permissions.length > 3 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                  +{access.permissions.length - 3}
                </Badge>
              )}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
          </div>

          <p className="text-[10px] text-muted-foreground mt-2">
            {ACCESS_LEVELS.find((l) => l.key === access.access_level)?.label || access.access_level} • Requested {new Date(access.requested_at).toLocaleDateString("en-IN")}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function CompanyDetailView({ access, activities, onBack, onLogActivity }: {
  access: CompanyAccess;
  activities: Activity[];
  onBack: () => void;
  onLogActivity: (type: string, desc: string) => void;
}) {
  const status = statusConfig[access.status] || statusConfig.pending;
  const isApproved = access.status === "approved";

  const quickActions = [
    { key: "view_accounting", label: "View Ledgers", icon: Eye, action: "Viewed accounting ledgers" },
    { key: "manage_gst", label: "GST Filing", icon: ShieldCheck, action: "Accessed GST filing module" },
    { key: "approve_entries", label: "Approve Entries", icon: CheckCircle2, action: "Opened entry approval queue" },
    { key: "generate_reports", label: "Generate Report", icon: BarChart3, action: "Generated financial report" },
  ];

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground">
        ← Back to Clients
      </Button>

      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Building2 className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">{access.company_name}</h1>
                {access.company_gstin && (
                  <code className="text-xs bg-muted px-2 py-0.5 rounded">{access.company_gstin}</code>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline" className={`text-xs gap-1 ${status.cls}`}>
                    {status.label}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {ACCESS_LEVELS.find((l) => l.key === access.access_level)?.label || access.access_level}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="mt-4 pt-4 border-t border-border/30">
            <p className="text-xs font-semibold text-foreground mb-2">Granted Permissions</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PERMISSION_OPTIONS.map((p) => {
                const hasPermission = access.permissions.includes(p.key);
                const Icon = p.icon;
                return (
                  <div key={p.key} className={`flex items-center gap-2 rounded-lg border p-2 text-xs ${hasPermission ? "border-accent/30 bg-accent/5" : "border-border/30 bg-muted/20 opacity-40"}`}>
                    <Icon className={`h-3.5 w-3.5 ${hasPermission ? "text-accent" : "text-muted-foreground"}`} />
                    <span className={hasPermission ? "text-foreground" : "text-muted-foreground"}>{p.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {isApproved && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.filter((a) => access.permissions.includes(a.key)).map((a) => {
            const Icon = a.icon;
            return (
              <Card
                key={a.key}
                className="bg-card border-border hover:border-accent/30 cursor-pointer transition-all"
                onClick={() => onLogActivity(a.key, a.action)}
              >
                <CardContent className="p-4 flex flex-col items-center gap-2 text-center">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <span className="text-xs font-medium text-foreground">{a.label}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!isApproved && (
        <Card className="bg-yellow-500/5 border-yellow-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Access {access.status === "pending" ? "Pending Approval" : "Not Available"}</p>
              <p className="text-xs text-muted-foreground">
                {access.status === "pending"
                  ? "Waiting for the company admin to approve your access request."
                  : "Your access to this company workspace has been revoked or rejected."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      <Card className="bg-card border-border">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-xs font-semibold">Activity Log</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {activities.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No activity recorded yet</p>
          ) : (
            <div className="space-y-2">
              {activities.map((a) => (
                <div key={a.id} className="flex items-center gap-3 text-xs py-1.5 border-b border-border/20 last:border-0">
                  <span className="text-muted-foreground w-28 flex-shrink-0">
                    {new Date(a.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">{a.activity_type}</Badge>
                  <span className="text-foreground">{a.description}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const CAClientManagementPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [accessList, setAccessList] = useState<CompanyAccess[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccess, setSelectedAccess] = useState<CompanyAccess | null>(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [tab, setTab] = useState("approved");

  // Request form
  const [reqCompanyName, setReqCompanyName] = useState("");
  const [reqGstin, setReqGstin] = useState("");
  const [reqNotes, setReqNotes] = useState("");
  const [reqAccessLevel, setReqAccessLevel] = useState("view_only");
  const [reqPermissions, setReqPermissions] = useState<string[]>(["view_accounting"]);
  const [reqCustomPermissions, setReqCustomPermissions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("ca_company_access")
      .select("*")
      .order("requested_at", { ascending: false });
    if (data) setAccessList(data as CompanyAccess[]);

    const { data: acts } = await supabase
      .from("ca_client_activities")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (acts) setActivities(acts as Activity[]);

    setLoading(false);
  };

  const togglePermission = (key: string) => {
    setReqPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const submitAccessRequest = async () => {
    if (!user || !reqCompanyName.trim()) {
      toast({ title: "Company name is required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("ca_company_access").insert({
      ca_user_id: user.id,
      company_name: reqCompanyName.trim(),
      company_gstin: reqGstin.trim() || null,
      access_level: reqAccessLevel,
      permissions: reqPermissions,
      notes: reqNotes.trim() || null,
    } as any);

    if (error) {
      toast({ title: "Failed to submit request", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Access request sent!", description: "The company admin will review your request." });
      setShowRequestDialog(false);
      setReqCompanyName(""); setReqGstin(""); setReqNotes(""); setReqAccessLevel("view_only"); setReqPermissions(["view_accounting"]); setReqCustomPermissions(false);
      fetchData();
    }
    setSubmitting(false);
  };

  const logActivity = async (type: string, desc: string) => {
    if (!user || !selectedAccess) return;
    await supabase.from("ca_client_activities").insert({
      ca_user_id: user.id,
      access_id: selectedAccess.id,
      activity_type: type,
      description: desc,
    } as any);
    toast({ title: desc });
    fetchData();
  };

  const approvedClients = accessList.filter((a) => a.status === "approved");
  const pendingClients = accessList.filter((a) => a.status === "pending");
  const otherClients = accessList.filter((a) => !["approved", "pending"].includes(a.status));

  if (selectedAccess) {
    const accessActivities = activities.filter((a) => a.access_id === selectedAccess.id);
    return (
      <CompanyDetailView
        access={selectedAccess}
        activities={accessActivities}
        onBack={() => setSelectedAccess(null)}
        onLogActivity={logActivity}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-accent" />
            Client Companies
          </h1>
          <p className="text-sm text-muted-foreground">Manage your client company workspaces</p>
        </div>
        <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogTrigger asChild>
            <Button className="gap-1.5">
              <Plus className="h-4 w-4" /> Request Access
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Company Access</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">Company Name *</Label>
                <Input value={reqCompanyName} onChange={(e) => setReqCompanyName(e.target.value)} placeholder="e.g., Reliance Industries Ltd" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">GSTIN</Label>
                <Input value={reqGstin} onChange={(e) => setReqGstin(e.target.value)} placeholder="22AAAAA0000A1Z5" maxLength={15} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Permission Level *</Label>
                <div className="space-y-2 mt-2">
                  {ACCESS_LEVELS.map((level) => {
                    const Icon = level.icon;
                    const isSelected = reqAccessLevel === level.key;
                    return (
                      <div
                        key={level.key}
                        className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-all ${isSelected ? "border-accent bg-accent/5" : "border-border hover:border-accent/30"}`}
                        onClick={() => {
                          setReqAccessLevel(level.key);
                          if (!reqCustomPermissions) setReqPermissions(level.permissions);
                        }}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-accent/10" : "bg-muted"}`}>
                          <Icon className={`h-4 w-4 ${isSelected ? "text-accent" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{level.label}</p>
                          <p className="text-[10px] text-muted-foreground">{level.desc}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {level.permissions.map((p) => (
                              <Badge key={p} variant="outline" className="text-[9px] h-4">{PERMISSION_OPTIONS.find((o) => o.key === p)?.label || p}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox
                    checked={reqCustomPermissions}
                    onCheckedChange={(v) => {
                      setReqCustomPermissions(!!v);
                      if (!v) {
                        const level = ACCESS_LEVELS.find((l) => l.key === reqAccessLevel);
                        if (level) setReqPermissions(level.permissions);
                      }
                    }}
                    id="custom-perms"
                  />
                  <label htmlFor="custom-perms" className="text-xs text-muted-foreground cursor-pointer">Customize individual permissions</label>
                </div>
                {reqCustomPermissions && (
                  <div className="space-y-2 pl-6">
                    {PERMISSION_OPTIONS.map((p) => (
                      <div key={p.key} className="flex items-start gap-2">
                        <Checkbox
                          checked={reqPermissions.includes(p.key)}
                          onCheckedChange={() => togglePermission(p.key)}
                          id={p.key}
                        />
                        <div>
                          <label htmlFor={p.key} className="text-xs font-medium text-foreground cursor-pointer">{p.label}</label>
                          <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} placeholder="Additional context for the company admin..." rows={2} className="mt-1 text-xs" />
              </div>
              <Button onClick={submitAccessRequest} disabled={submitting} className="w-full gap-1.5">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{approvedClients.length}</p>
              <p className="text-xs text-muted-foreground">Active Clients</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pendingClients.length}</p>
              <p className="text-xs text-muted-foreground">Pending Requests</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{accessList.length}</p>
              <p className="text-xs text-muted-foreground">Total Companies</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client List */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="approved">Active ({approvedClients.length})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({pendingClients.length})</TabsTrigger>
          {otherClients.length > 0 && <TabsTrigger value="other">Other ({otherClients.length})</TabsTrigger>}
        </TabsList>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border animate-pulse">
                <CardContent className="p-4 h-[140px]" />
              </Card>
            ))}
          </div>
        ) : (
          <>
            <TabsContent value="approved" className="mt-4">
              {approvedClients.length === 0 ? (
                <div className="text-center py-16">
                  <Building2 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">No active clients</h3>
                  <p className="text-xs text-muted-foreground">Request access to a company workspace to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {approvedClients.map((a) => (
                    <CompanyCard key={a.id} access={a} onView={() => setSelectedAccess(a)} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="pending" className="mt-4">
              {pendingClients.length === 0 ? (
                <div className="text-center py-16">
                  <Clock className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1">No pending requests</h3>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingClients.map((a) => (
                    <CompanyCard key={a.id} access={a} onView={() => setSelectedAccess(a)} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="other" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {otherClients.map((a) => (
                  <CompanyCard key={a.id} access={a} onView={() => setSelectedAccess(a)} />
                ))}
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
};

export default CAClientManagementPage;
